// Based mostly on https://github.com/Uniswap/interface/blob/main/src/theme/index.tsx

export const colors = {
  white: '#FFFFFF',
  black: '#000000',
  gray50: '#F5F6FC',
  gray100: '#E8ECFB',
  gray150: '#D2D9EE',
  gray200: '#B8C0DC',
  gray250: '#A6AFCA',
  gray300: '#98A1C0',
  gray350: '#888FAB',
  gray400: '#7780A0',
  gray450: '#6B7594',
  gray500: '#5D6785',
  gray550: '#505A78',
  gray600: '#404A67',
  gray650: '#333D59',
  gray700: '#293249',
  gray750: '#1B2236',
  gray800: '#131A2A',
  gray850: '#0E1524',
  gray900: '#0D111C',
  gray950: '#080B11',
  pink50: '#F9ECF1',
  pink100: '#FFD9E4',
  pink200: '#FBA4C0',
  pink300: '#FF6FA3',
  pink400: '#111111',
  pink500: '#C41969',
  pink600: '#8C0F49',
  pink700: '#55072A',
  pink800: '#350318',
  pink900: '#2B000B',
  pinkVibrant: '#F51A70',
  red50: '#FAECEA',
  red100: '#FED5CF',
  red200: '#FEA79B',
  red300: '#FD766B',
  red400: '#FA2B39',
  red500: '#C4292F',
  red600: '#891E20',
  red700: '#530F0F',
  red800: '#380A03',
  red900: '#240800',
  redVibrant: '#F14544',
  yellow50: '#F6F2D5',
  yellow100: '#DBBC19',
  yellow200: '#DBBC19',
  yellow300: '#BB9F13',
  yellow400: '#A08116',
  yellow500: '#866311',
  yellow600: '#5D4204',
  yellow700: '#3E2B04',
  yellow800: '#231902',
  yellow900: '#180F02',
  yellowVibrant: '#FAF40A',
  // TODO: add gold 50-900
  gold50: '#FFF9E6',
  gold100: '#FFF2CC',  // goldLight
  gold200: '#FFD700',  // gold
  gold300: '#E6C200',
  gold400: '#C5A44D',  // goldMuted
  gold500: '#8B7355',  // goldDark
  gold600: '#614E1A',  // your primary4
  gold700: '#443914',  // your primary5
  gold800: '#2A2411',
  gold900: '#1A160B',
  goldVibrant: '#FFD700',
  green50: '#E3F3E6',
  green100: '#BFEECA',
  green200: '#76D191',
  green300: '#40B66B',
  green400: '#209853',
  green500: '#0B783E',
  green600: '#0C522A',
  green700: '#053117',
  green800: '#091F10',
  green900: '#09130B',
  greenVibrant: '#5CFE9D',
  blue50: '#EDEFF8',
  blue100: '#DEE1FF',
  blue200: '#ADBCFF',
  blue300: '#869EFF',
  blue400: '#4C82FB',
  blue500: '#1267D6',
  blue600: '#1D4294',
  blue700: '#09265E',
  blue800: '#0B193F',
  blue900: '#040E34',
  blueVibrant: '#587BFF',
  // TODO: add magenta 50-900
  magenta300: '#FD82FF',
  magentaVibrant: '#75ff72',
  purple300: '#8440F2',
  purple900: '#1C0337',
  purpleVibrant: '#6100FF',
  // TODO: add all other vibrant variations
  networkEthereum: '#627EEA',
  networkOptimism: '#FF0420',
  networkOptimismSoft: 'rgba(255, 4, 32, 0.16)',
  networkPolygon: '#A457FF',
  networkArbitrum: '#28A0F0',
  networkBsc: '#F0B90B',
  networkPolygonSoft: 'rgba(164, 87, 255, 0.16)',
  networkEthereumSoft: 'rgba(98, 126, 234, 0.16)',
  networkBase: '#0052FF',
  networkBerachain: '#8B572A',
  networkHyperliquid: '#1C0337',
  networkSonic: '#F5F5F5',
  networkKatana: '#4477FF',
  networkMitosis: '#4169E1',
  networkSomnia: '#8B00FF',
  networkZerog: '#00FF00',
  networkPlasma: '#6600CC',
  networkMonad: '#4B0082',
  networkMegaeth: '#627EEA',
  networkStable: '#26A17B',
  //NEW COLORS FOR SPORE - need to define light/dark here cause they are root colors now (different system)

  // Dark theme colors
  neutral1_dark: '#FFF2CC',  // goldLight (text1)
  neutral2_dark: '#C5A44D',  // goldMuted (text2)
  neutral3_dark: '#8B7355',  // goldDark (text3)
  surface1_dark: '#080808',  // darkerBg
  surface2_dark: '#0F0F0F',  // darkBg
  surface3_dark: '#141414',  // bg1
  surface4_dark: '#1A1A1A',  // bg2
  surface5_dark: '#202020',  // bg3
  accent1_dark: '#E6C200',   // gold (primary1)
  accent2_dark: '#614E1A',   // primary4

  // Light theme colors
  neutral1_light: '#614E1A',  // Darker gold for text on light bg
  neutral2_light: '#8B7355',  // goldDark
  neutral3_light: '#C5A44D',  // goldMuted
  surface1_light: '#FFFFFF',
  surface2_light: '#FFF9E6',  // Very light gold tint
  surface3_light: '#FFF2CC',  // goldLight
  surface4_light: '#FFE7A3',  // Light gold
  surface5_light: '#FFD700',  // gold
  accent1_light: '#614E1A',   // primary4
  accent2_light: '#FFD700',   // gold

  success: '#40B66B',
  critical: '#FF5F52',
  scrim: 'rgba(0, 0, 0, 0.60)',
}

type Theme = typeof darkTheme

const commonTheme = {
  white: colors.white,
  black: colors.black,

  chain_1: colors.networkEthereum,
  chain_3: colors.yellow400,
  chain_4: colors.pink400,
  chain_5: colors.green400,
  chain_10: colors.networkOptimism,
  chain_137: colors.networkPolygon,
  chain_42: colors.networkArbitrum,
  chain_56: colors.networkBsc,
  chain_420: colors.networkOptimism,
  chain_42161: colors.networkArbitrum,
  chain_421613: colors.networkArbitrum,
  chain_80001: colors.networkPolygon,
  chain_43114: colors.networkOptimism,
  chain_137_background: colors.purple900,
  chain_10_background: colors.red900,
  chain_43114_background: colors.red900,
  chain_42161_background: colors.blue900,
  chain_84531: colors.networkBase,
  chain_80094: colors.networkBerachain,
  chain_999: colors.networkHyperliquid,
  chain_146: colors.networkSonic,
  chain_747474: colors.networkKatana,
  chain_124816: colors.networkMitosis,
  chain_5031: colors.networkSomnia,
  chain_16661: colors.networkZerog,
  chain_9745: colors.networkPlasma,
  chain_10143: colors.networkMonad,
  chain_143: colors.networkMonad,
  chain_4326: colors.networkMegaeth,
  chain_988: colors.networkStable,
  chain_56_background: colors.networkBsc,
  promotional: colors.magenta300,

  brandedGradient: 'linear-gradient(139.57deg, #FF79C9 4.35%, #FFB8E2 96.44%);',
  promotionalGradient: colors.accent1_light,
}

export const darkTheme = {
  ...commonTheme,

  background: colors.black,

  neutral1: colors.neutral1_dark,
  neutral2: colors.neutral2_dark,
  neutral3: colors.neutral3_dark,

  surface1: colors.surface1_dark,
  surface2: colors.surface2_dark,
  surface3: colors.surface3_dark,
  surface4: colors.surface4_dark,
  surface5: colors.surface5_dark,

  accent1: colors.accent1_dark,
  accent2: colors.accent2_dark,
  success: colors.success,
  critical: colors.critical,
  scrim: colors.scrim,
}

export const lightTheme: Theme = {
  ...commonTheme,

  background: colors.white,

  neutral1: colors.neutral1_light,
  neutral2: colors.neutral2_light,
  neutral3: colors.neutral3_light,

  surface1: colors.surface1_light,
  surface2: colors.surface2_light,
  surface3: colors.surface3_light,
  surface4: colors.surface4_light,
  surface5: colors.surface5_light,

  accent1: colors.accent1_light,
  accent2: colors.accent2_light,

  success: colors.success,
  critical: colors.critical,
  scrim: colors.scrim,
}
